import React from 'react';

/**
 * Skeleton loading placeholder — shows animated shimmer blocks while data loads.
 *
 * Usage:
 *   <Skeleton width="100%" height={20} />
 *   <Skeleton variant="card" />
 *   <Skeleton variant="table" rows={5} />
 *   <Skeleton variant="stat" />
 */

const shimmerStyle = {
  background: 'linear-gradient(90deg, var(--border, #e5e7eb) 25%, var(--surface-2, #f3f4f6) 50%, var(--border, #e5e7eb) 75%)',
  backgroundSize: '200% 100%',
  animation: 'skeleton-shimmer 1.4s infinite',
  borderRadius: 6,
  display: 'block',
};

// Inject keyframes once
if (typeof document !== 'undefined' && !document.getElementById('skeleton-styles')) {
  const style = document.createElement('style');
  style.id = 'skeleton-styles';
  style.textContent = `
    @keyframes skeleton-shimmer {
      0%   { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
  `;
  document.head.appendChild(style);
}

export function SkeletonLine({ width = '100%', height = 16, style = {} }) {
  return (
    <span style={{ ...shimmerStyle, width, height, ...style }} />
  );
}

export function SkeletonStat() {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '1.25rem 1.5rem' }}>
      <SkeletonLine width={80} height={13} style={{ marginBottom: 12 }} />
      <SkeletonLine width={120} height={28} style={{ marginBottom: 8 }} />
      <SkeletonLine width={100} height={12} />
    </div>
  );
}

export function SkeletonCard() {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '1.25rem' }}>
      <SkeletonLine width="60%" height={14} style={{ marginBottom: 12 }} />
      <SkeletonLine width="100%" height={12} style={{ marginBottom: 6 }} />
      <SkeletonLine width="85%" height={12} style={{ marginBottom: 6 }} />
      <SkeletonLine width="70%" height={12} />
    </div>
  );
}

export function SkeletonTable({ rows = 5, cols = 5 }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {Array.from({ length: cols }).map((_, i) => (
              <th key={i} style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
                <SkeletonLine width={60 + i * 15} height={13} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, r) => (
            <tr key={r} style={{ borderBottom: '1px solid var(--border-light, #f3f4f6)' }}>
              {Array.from({ length: cols }).map((_, c) => (
                <td key={c} style={{ padding: '10px 12px' }}>
                  <SkeletonLine width={`${55 + ((r + c) % 4) * 10}%`} height={13} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SkeletonDashboard() {
  return (
    <div>
      {/* Stat cards row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {[1, 2, 3, 4].map(i => <SkeletonStat key={i} />)}
      </div>
      {/* Chart area */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
        {[1, 2].map(i => (
          <div key={i} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '1.25rem' }}>
            <SkeletonLine width={140} height={14} style={{ marginBottom: 16 }} />
            <SkeletonLine width="100%" height={180} style={{ borderRadius: 8 }} />
          </div>
        ))}
      </div>
      {/* Table area */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '1.25rem' }}>
        <SkeletonLine width={160} height={14} style={{ marginBottom: 16 }} />
        <SkeletonTable rows={4} cols={5} />
      </div>
    </div>
  );
}

// Default export: generic skeleton line
export default function Skeleton({ width = '100%', height = 16, variant, rows, style }) {
  if (variant === 'stat')      return <SkeletonStat />;
  if (variant === 'card')      return <SkeletonCard />;
  if (variant === 'dashboard') return <SkeletonDashboard />;
  if (variant === 'table')     return <SkeletonTable rows={rows} />;
  return <SkeletonLine width={width} height={height} style={style} />;
}
